import { computed, Signal, signal } from "@preact/signals"
import cbor from "cbor-js"

const PayloadCrypto = {
    encode: (payload: Record<string, unknown>): string => {
        // 使用 Uint8Array 而不是 Buffer
        const encoded = cbor.encode(payload)
        // 將二進制數據轉換為正確的 base64 字符串
        const uint8ArrayString = Array.from(new Uint8Array(encoded))
            .map((b) => String.fromCharCode(b))
            .join("")
        return btoa(uint8ArrayString)
    },
    decode: (base64: string): Record<string, unknown> => {
        try {
            // 從 base64 轉換為二進制數據
            const binary = atob(base64)
            const bytes = new Uint8Array(binary.length)
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i)
            }
            // 使用 cbor 解碼二進制數據
            return cbor.decode(bytes.buffer)
        } catch (error) {
            console.error("PayloadCrypto.decode failed:", error)
            return {}
        }
    },
    jsonParse: (json?: string): Record<string, unknown> => {
        try {
            return JSON.parse(json || "{}")
        } catch (error) {
            console.error("PayloadCrypto.jsonParse failed:", error)
            return {}
        }
    },
    jsonStringify: (payload: Record<string, unknown>): string => {
        try {
            return JSON.stringify(payload)
        } catch (error) {
            console.error("PayloadCrypto.jsonStringify failed:", error)
            return "{}"
        }
    },
    kSalt: btoa("filbet"),
    encodeKey: (key: string) => {
        const base64Key = PayloadCrypto.encode({ data: key })
        return base64Key
    },
    decodeKey: (key: string) => {
        if (import.meta.env.MODE === "development") {
            return key
        }
        return PayloadCrypto.decode(key).data as string
    },
    createStorageKey: (key: string) => {
        const _key = `signal_${__APP_VERSION__}_${key}`
        if (import.meta.env.MODE === "development") {
            return _key
        }
        const encodedKey = PayloadCrypto.encodeKey(_key)
        return encodedKey
    },
}

function clearLastStorage(storage: Storage) {
    const keys = Object.keys(storage)
    keys.forEach((key) => {
        try {
            const decodedKey = PayloadCrypto.decodeKey(key)
            const [, hash] = decodedKey.match(/signal_([^_]+)_/) ?? []
            if (hash && hash !== __APP_VERSION__) {
                storage.removeItem(key)
                console.log(`clearLastStorage: ${key}`)
            }
        } catch {
            storage.removeItem(key)
        }
    })
}

clearLastStorage(localStorage)

/**
 * 創建一個與 localStorage 同步的 Signal，每次讀取都會從 storage 獲取最新值
 * @param initialValue 初始值
 * @param key 存儲鍵名
 * @returns 增強的 Signal 實例
 */
export function signalWithStorage<T>(
    initialValue: T,
    key: string,
    storage: Storage = localStorage,
) {
    const storageKey = PayloadCrypto.createStorageKey(key)

    // 從 storage 讀取值的函數
    const getFromStorage = (): T => {
        try {
            const stored = storage.getItem(storageKey) as string | null
            if (!stored) return initialValue
            const parser = import.meta.env.DEV ? PayloadCrypto.jsonParse : PayloadCrypto.decode
            const storedDecoded = parser(stored) as unknown as { data: T }
            return storedDecoded?.data ?? initialValue
        } catch {
            return initialValue
        }
    }

    // 創建基礎 signal
    const baseSignal = signal<T>(getFromStorage())

    baseSignal.subscribe((value) => {
        const parser = import.meta.env.DEV ? PayloadCrypto.jsonStringify : PayloadCrypto.encode
        storage.setItem(storageKey, parser({ data: value }))
    })

    return baseSignal
}

/**
 * 創建一個與 Postman API 同步的 Signal
 * @param api - 要同步的 Postman API
 * @param initialValue - 初始值
 * @param opts - 選項
 * @returns 包含 Signal 實例和 fetch 函數的對象
 */
export function signalWithPostmanApi<Props, Result>(
    api: (props?: Props) => Promise<Result>,
    opts?: {
        defaultValue?: Result
        storageKey?: string
        storage?: Storage
        connectSignal?: Signal<Result | undefined>
    },
) {
    const [s, clean] = (() => {
        if (opts?.storageKey) {
            const s = signalWithStorage(opts?.defaultValue, `api_${opts.storageKey}`, opts.storage)
            return [s, () => {}] as const
        }
        return [signal(opts?.defaultValue), () => {}]
    })()

    const fetch = async (props?: Props) => {
        const result = await api(props)
        s.value = result
        if (opts?.connectSignal) {
            opts.connectSignal.value = result
        }
    }

    return [s, fetch, clean] as const
}
